import { useState } from "react";
import { useAuth } from "../app/auth";

function BrandMark({ size = 30 }: { size?: number }) {
  return (
    <svg viewBox="0 0 40 40" width={size} height={size} aria-hidden="true">
      <defs>
        <radialGradient id="lc" cx="50%" cy="50%" r="50%">
          <stop offset="55%" stopColor="#F2A73B" stopOpacity="0" />
          <stop offset="80%" stopColor="#F2A73B" stopOpacity="0.7" />
          <stop offset="100%" stopColor="#F2A73B" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill="url(#lc)" />
      <circle cx="20" cy="20" r="11" fill="#0C0D13" stroke="#F2A73B" strokeWidth="1.4" />
      <circle cx="24.5" cy="16" r="11" fill="#0C0D13" />
    </svg>
  );
}

const GoogleG = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18Z" />
    <path fill="#FBBC05" d="M3.98 10.72a5.4 5.4 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34Z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.47.9 11.43 0 9 0A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58Z" />
  </svg>
);
const MsLogo = () => (
  <svg width="16" height="16" viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

export default function Login() {
  const auth = useAuth();
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState<{ link?: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setErr(null);
    setBusy(true);
    const r = await auth.email(email);
    setBusy(false);
    if (r.devLink) setSent({ link: r.devLink });
    else setSent({});
  };

  const signInSso = async (provider: "google" | "microsoft") => {
    setErr(null);
    try {
      await auth.sso(provider);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Sign-in failed. Please try again.");
    }
  };

  return (
    <div className="auth">
      <aside className="auth__brand">
        <div className="auth__eclipse" aria-hidden="true"><i /><span /></div>
        <div className="auth__brandin">
          <div className="auth__logo"><BrandMark size={26} /><b>Penumbrai</b></div>
          <h1>Customer success,<br />out of the shadows.</h1>
          <p>Stand up your workspace in minutes. Bring your data, model it your way, and catch risk while it's still faint. No demos, no sales calls.</p>
          <ul className="auth__points">
            <li>Free to start — no credit card</li>
            <li>Your whole team, self-serve</li>
            <li>Live in an afternoon</li>
          </ul>
        </div>
      </aside>

      <main className="auth__panel">
        <div className="auth__card">
          <div className="auth__mark"><BrandMark size={34} /></div>
          <h2>{mode === "signup" ? "Create your workspace" : "Welcome back"}</h2>
          <p className="auth__sub">{mode === "signup" ? "Start free in seconds." : "Sign in to your workspace."}</p>

          {sent ? (
            <div className="auth__sent">
              <b>Check your email</b>
              <p className="muted">We sent a sign-in link to <b>{email}</b>.</p>
              {sent.link && <a className="btn btn--primary" style={{ width: "100%", justifyContent: "center", marginTop: 10 }} href={sent.link}>Open magic link →</a>}
              <button className="auth__link" onClick={() => setSent(null)}>Use a different email</button>
            </div>
          ) : (
            <>
              <button className="auth__sso" onClick={() => signInSso("google")}><GoogleG /> Continue with Google</button>
              <button className="auth__sso" onClick={() => signInSso("microsoft")}><MsLogo /> Continue with Microsoft</button>

              {err && <p className="auth__err" role="alert">{err}</p>}

              <div className="auth__or"><span>or</span></div>

              <form onSubmit={submitEmail}>
                <input className="input auth__email" type="email" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Work email" />
                <button className="btn btn--primary" type="submit" disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
                  {busy ? "Sending…" : "Continue with email"}
                </button>
              </form>

              {auth.config?.devAllowed !== false && (
                <button className="auth__demo" onClick={() => auth.demo()}>Continue with a demo account →</button>
              )}
            </>
          )}

          <p className="auth__legal">
            By continuing you agree to the Terms and Privacy Policy.
          </p>
        </div>

        <p className="auth__toggle">
          {mode === "signup" ? "Already have a workspace?" : "New to Penumbrai?"}{" "}
          <button className="auth__link" onClick={() => setMode(mode === "signup" ? "signin" : "signup")}>
            {mode === "signup" ? "Sign in" : "Start free"}
          </button>
        </p>
      </main>
    </div>
  );
}
